<template>
  <div id="admin-contacts-page">
    <Navigation />

    <section class="admin-section">
      <div class="container">
        <header class="admin-header">
          <h1 class="admin-title">商务联系表单管理</h1>
          <p class="admin-subtitle">查看通过官网提交的商务联系信息，仅供内部使用。</p>
        </header>

        <!-- 简单口令校验（前端层），配合路由守卫使用 -->
        <div v-if="!isAuthorized" class="auth-card">
          <h2 class="auth-title">管理员验证</h2>
          <p class="auth-desc">请输入十五位内部管理口令后查看提交记录。</p>
          <input
            v-model="passwordInput"
            type="password"
            class="auth-input"
            placeholder="输入管理口令"
            @keyup.enter="handleAuth"
          />
          <button class="auth-button" @click="handleAuth">进入管理页面</button>
          <p v-if="authError" class="auth-error">{{ authError }}</p>
          <p class="auth-tip">建议在线上环境再配合服务端或网关级别的访问控制（如 VPN / IP 白名单）。</p>
        </div>

        <div v-else class="admin-card">
          <div class="admin-card-header">
            <h2 class="card-title">提交记录</h2>
            <span class="card-meta">
              共 {{ submissions.length }} 条
            </span>
          </div>

          <div v-if="isLoading" class="state-text">加载中...</div>
          <div v-else-if="error" class="state-text error">{{ error }}</div>
          <div v-else-if="submissions.length === 0" class="state-text">当前还没有提交记录。</div>
          <div v-else class="table-wrapper">
            <table class="submissions-table">
              <thead>
                <tr>
                  <th>时间</th>
                  <th>姓名</th>
                  <th>邮箱</th>
                  <th>公司</th>
                  <th>联系目的</th>
                  <th>留言内容</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in orderedSubmissions"
                  :key="item.id"
                  class="row-click"
                  @click="openDetail(item)"
                >
                  <td class="col-time">
                    {{ formatDate(item.createdAt) }}
                  </td>
                  <td>{{ item.name }}</td>
                  <td>{{ item.email }}</td>
                  <td>{{ item.company }}</td>
                  <td>{{ mapPurpose(item.purpose) }}</td>
                  <td class="col-message">
                    <div class="message-text">
                      {{ item.message || '-' }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- 详情弹层 -->
    <div v-if="selected" class="detail-overlay" @click.self="closeDetail">
      <div class="detail-card">
        <div class="detail-header">
          <h2>表单详情</h2>
          <button class="detail-close" @click="closeDetail">关闭</button>
        </div>
        <div class="detail-body">
          <div class="detail-row">
            <span class="detail-label">时间</span>
            <span class="detail-value">{{ formatDate(selected.createdAt) }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">姓名</span>
            <span class="detail-value">{{ selected.name }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">邮箱</span>
            <span class="detail-value">{{ selected.email }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">公司</span>
            <span class="detail-value">{{ selected.company }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">联系目的</span>
            <span class="detail-value">{{ mapPurpose(selected.purpose) }}</span>
          </div>
          <div class="detail-row detail-message">
            <span class="detail-label">留言内容</span>
            <div class="detail-message-box">
              {{ selected.message || '-' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <FooterSection />
  </div>
</template>

<script>
import Navigation from '@/components/Navigation.vue'
import FooterSection from '@/components/FooterSection.vue'

const ADMIN_KEY = process.env.VUE_APP_ADMIN_KEY || 'seekreal-admin-2026-xiaqixuan-xuan-tongxiaobai-xiaxinyu-wangxuan-liudaoqiang'

export default {
  name: 'AdminContacts',
  components: {
    Navigation,
    FooterSection
  },
  data() {
    return {
      submissions: [],
      isLoading: false,
      error: '',
      isAuthorized: false,
      passwordInput: '',
      authError: '',
      selected: null
    }
  },
  computed: {
    orderedSubmissions() {
      return [...this.submissions].sort((a, b) => {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      })
    }
  },
  async mounted() {
    const authed = typeof window !== 'undefined' && window.sessionStorage
      ? window.sessionStorage.getItem('adminContactsAuthed') === '1'
      : false

    if (authed) {
      this.isAuthorized = true
      this.fetchSubmissions()
    }
  },
  methods: {
    async fetchSubmissions() {
      this.isLoading = true
      this.error = ''
      try {
        const res = await fetch('/api/contact-submissions')
        if (!res.ok) {
          throw new Error('加载失败')
        }
        const data = await res.json()
        this.submissions = Array.isArray(data) ? data : []
      } catch (e) {
        this.error = '加载提交记录失败，请稍后重试。'
      } finally {
        this.isLoading = false
      }
    },
    handleAuth() {
      if (!this.passwordInput) {
        this.authError = '请输入十五位管理口令'
        return
      }
      if (this.passwordInput !== ADMIN_KEY) {
        this.authError = '管理口令错误'
        return
      }

      this.authError = ''
      this.isAuthorized = true
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem('adminContactsAuthed', '1')
      }
      this.fetchSubmissions()
    },
    formatDate(value) {
      if (!value) return '-'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mm = String(d.getMinutes()).padStart(2, '0')
      return `${y}-${m}-${day} ${hh}:${mm}`
    },
    mapPurpose(purpose) {
      switch (purpose) {
        case 'cooperation':
          return '商务合作'
        case 'purchase':
          return '企业采购'
        case 'integration':
          return '技术集成'
        case 'investment':
          return '投资交流'
        case 'media':
          return '媒体沟通'
        default:
          return purpose || '-'
      }
    },
    openDetail(item) {
      this.selected = item
    },
    closeDetail() {
      this.selected = null
    }
  }
}
</script>

<style scoped>
#admin-contacts-page {
  width: 100%;
  background: #0B1220;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 90px 2.5rem 70px;
}

.admin-header {
  margin-bottom: 32px;
}

.admin-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.admin-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.65);
}

.admin-card {
  background: #070B14;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 2rem 2.4rem 2.4rem;
}

.admin-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.card-meta {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
}

.state-text {
  padding: 1.5rem 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
}

.state-text.error {
  color: #f97373;
}

.table-wrapper {
  margin-top: 0.5rem;
  overflow-x: auto;
}

.submissions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.95rem;
}

.submissions-table th,
.submissions-table td {
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.3);
  text-align: left;
}

.submissions-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(148, 163, 184, 0.9);
}

.submissions-table tbody tr:nth-child(even) {
  background: rgba(15, 23, 42, 0.5);
}

.row-click {
  cursor: pointer;
  transition: background 0.15s ease, transform 0.05s ease;
}

.row-click:hover {
  background: rgba(59, 130, 246, 0.18);
  transform: translateY(-1px);
}

.col-time {
  white-space: nowrap;
}

.col-message {
  max-width: 380px;
}

.message-text {
  max-height: 4.5rem;
  overflow: auto;
}

/* 管理员口令卡片 */
.auth-card {
  background: #070B14;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 2rem 2.4rem 2.4rem;
  max-width: 520px;
}

.auth-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin-bottom: 0.6rem;
}

.auth-desc {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.4rem;
}

.auth-input {
  width: 100%;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(15, 23, 42, 0.7);
  color: white;
  outline: none;
  margin-bottom: 0.9rem;
}

.auth-input:focus {
  border-color: #60a5fa;
}

.auth-button {
  display: inline-block;
  padding: 0.6rem 1.4rem;
  border-radius: 999px;
  border: none;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0.6rem;
}

.auth-error {
  font-size: 0.85rem;
  color: #f97373;
  margin-bottom: 0.3rem;
}

.auth-tip {
  font-size: 0.8rem;
  color: rgba(148, 163, 184, 0.8);
}

/* 详情弹层 */
.detail-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
}

.detail-card {
  width: min(640px, 92vw);
  max-height: 80vh;
  background: #020617;
  border-radius: 18px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.8);
  padding: 1.6rem 1.8rem 1.8rem;
  display: flex;
  flex-direction: column;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.detail-header h2 {
  font-size: 1.2rem;
}

.detail-close {
  border: none;
  background: transparent;
  color: rgba(148, 163, 184, 0.9);
  cursor: pointer;
  font-size: 0.9rem;
}

.detail-body {
  font-size: 0.9rem;
  overflow-y: auto;
  padding-right: 0.4rem;
}

.detail-row {
  display: grid;
  grid-template-columns: 96px 1fr;
  column-gap: 1rem;
  margin-bottom: 0.75rem;
}

.detail-label {
  font-size: 0.8rem;
  color: rgba(148, 163, 184, 0.9);
}

.detail-value {
  color: #e5e7eb;
}

.detail-message {
  align-items: flex-start;
}

.detail-message-box {
  padding: 0.75rem 0.85rem;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.6);
  max-height: 220px;
  overflow-y: auto;
}

@media (max-width: 768px) {
  .container {
    padding: 70px 1.25rem 40px;
  }

  .admin-card {
    padding: 1.5rem 1.25rem 1.75rem;
  }
}
</style>

